import { useState, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import ClickAwayListener from '@mui/material/ClickAwayListener';
// icon
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PostAddIcon from '@mui/icons-material/PostAdd';
import BroadcastOnHomeIcon from '@mui/icons-material/BroadcastOnHome';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CommentIcon from '@mui/icons-material/Comment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

// ドローワー幅
const drawerWidth = 240;

// ※ 管理メニューは is_superuser のみ有効化（menuItems は Header 内で動的生成）

// ポップアップメニューアイテム
const popupItems = [
  { name: "logout", icon: <LogoutIcon />, label: "ログアウト", disabled: false },
];

// ドローワーヘッダ
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

// アプリケーションバー
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})< { open?: boolean } >(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// ヘッダコンテンツ
const Header = (props: { user: any | null | undefined }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isSuperUser = !!props.user?.is_superuser;
  const menuItems = [
    { path: "/",            icon: <DateRangeIcon />,          label: "日付",  disabled: false },
    { path: "/entry",       icon: <PostAddIcon />,            label: "登録",  disabled: false },
    { path: "/search",      icon: <FormatListBulletedIcon />, label: "検索 / 一覧",  disabled: false },
    { path: "/broadcast",   icon: <BroadcastOnHomeIcon />,    label: "放送",  disabled: false }, 
    { path: "/memo/search", icon: <CommentIcon />,            label: "メモ",  disabled: false },
    { path: "/manage#users",icon: <AdminPanelSettingsIcon />, label: "管理",  disabled: !isSuperUser },
  ];
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();

  // ドローワー開く
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  // ドローワー閉じる
  const handleDrawerClose = () => {
    setOpen(false);
  };

  // ドローワーメニュー選択
  const handleDrawerMenuItem = (index: number) => {
    setOpen(false);
    navigate(menuItems[index].path, { replace: true });
  };

  // ドローワーの外側クリックで閉じる（メニューボタンは除外）
  const handleDrawerClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as Node | null;
    if (menuButtonRef.current && target && menuButtonRef.current.contains(target)) {
      return; // メニューボタンのクリックは無視
    }
    setOpen(false);
  };

  // ホームへ戻るボタン
  const onClickHome = () => {
    navigate("/");
  };

  // ポップアップ開く
  const handlePopupOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // ポップアップ閉じる
  const handlePopupClose = () => {
    setAnchorEl(null);
  };

  // ポップアップメニュー選択
  const handlePopupMenuItem = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    // ポップアップ閉じる
    setAnchorEl(null);
    // ログアウト
    if (index === 0) {
        // 確認ダイアログを表示
        if (!window.confirm('ログアウトしてもよろしいですか？')) {
          return;
        }      
        // アクセストークンをローカルストレージから削除
        localStorage.removeItem('accesstoken');
        // ログイン画面へ遷移
        navigate("/login", {replace: true});
    }
  };

  // ポップアップ表示中
  const isPopupOpen = Boolean(anchorEl);
  // ポップアップID
  const popId = isPopupOpen ? 'account-popper' : undefined;

  // 文字列を色に変換
  function stringToColor(str: string) {
    let hash = 0;
    let i;
  
    /* eslint-disable no-bitwise */
    for (i = 0; i < str.length; i += 1) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
  
    let color = '#';
  
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */
  
    return color;
  };
  
  // 文字列をアバター(頭文字と色)に変換
  function stringAvatar(name: any) {
    if (name === null) {
      return "";
    }
    let arr = name.split(' ');
    var mark = "";
    for (var i = 0; i < arr.length; i ++) {
      mark += arr[i][0]
    }
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: mark,
    };
  };

  // レンダリング
  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed" open={open}>
        <Toolbar variant="dense">
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={() => handleDrawerOpen()}
            sx={{ mr: 2 }}
            ref={menuButtonRef}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            <Box component="img" src="/logo.png" alt="COVERAGE" 
              sx={{ m: 1, height: "64px", width: "auto", "&:hover": { cursor: "pointer" } }} 
              onClick={() => onClickHome()}
            />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              COVERAGE
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="account of current user"
            aria-describedby={popId}
            onClick={handlePopupOpen}
            sx={{ mr: 2 }}
          >
            <Avatar {...stringAvatar(props.user ? props.user.username : "")} />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ mr: 2 }}>
            {props.user ? props.user.username : ""}
          </Typography>
        </Toolbar>
      </AppBar>
      <ClickAwayListener onClickAway={handleDrawerClickAway} mouseEvent="onClick" touchEvent="onTouchStart">
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <DrawerHeader sx={{justifyContent: 'flex-end'}}>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeftIcon />
            </IconButton>
          </DrawerHeader>
          <Divider />
            <List>
              {menuItems.map((item, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton disabled={item.disabled} onClick={e => handleDrawerMenuItem(index)}>
                    <ListItemIcon>
                      {item.icon} 
                    </ListItemIcon>
                  <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
        </Drawer>
      </ClickAwayListener>      
      <Popover id={popId} open={isPopupOpen} anchorEl={anchorEl} 
        onClose={handlePopupClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }} 
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}>
        <Box sx={{width: 250, py:2, bgcolor: 'white'}}>
          <Stack sx={{ alignItems: "center" }} spacing={2}>
            <Avatar {...stringAvatar(props.user ? props.user.username : "")} />
            <Typography variant="h6" >
              {props.user ? props.user.username : ""}
            </Typography>
            <List>
              {popupItems.map((item, index) => (
                <ListItem key={item.name} disablePadding>
                  <ListItemButton disabled={item.disabled} onClick={e => handlePopupMenuItem(e, index)}>
                    <ListItemIcon>
                      {item.icon} 
                    </ListItemIcon>
                  <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Stack>
        </Box>          
      </Popover>
      <Toolbar />
    </Box>
  );
}

export default Header;
